import { Button } from '@workspace/ui';
import { createPortal } from 'react-dom';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useTour } from './TourProvider';
import type { TourStep } from './types';

type SpotlightRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

function resolveElement(step: TourStep | null, resolveAnchor: (anchorId?: string) => HTMLElement | null) {
  if (!step) {
    return null;
  }
  if (step.target.anchorId) {
    const anchor = resolveAnchor(step.target.anchorId);
    if (anchor) {
      return anchor;
    }
  }
  if (step.target.getElement) {
    const el = step.target.getElement();
    if (el) {
      return el;
    }
  }
  if (typeof document !== 'undefined' && step.target.selector) {
    return document.querySelector(step.target.selector) as HTMLElement | null;
  }
  return null;
}

function useSpotlightRect(step: TourStep | null, anchorVersion: number, resolveAnchor: TourOverlayProps['resolveAnchor']) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [rect, setRect] = useState<SpotlightRect | null>(null);

  useEffect(() => {
    setTarget(resolveElement(step, resolveAnchor));
  }, [step, anchorVersion, resolveAnchor]);

  useLayoutEffect(() => {
    if (!target) {
      setRect(null);
      return;
    }

    const update = () => {
      const bounds = target.getBoundingClientRect();
      const padding = step?.padding ?? 12;
      setRect({
        top: Math.max(bounds.top - padding, 0),
        left: Math.max(bounds.left - padding, 0),
        width: bounds.width + padding * 2,
        height: bounds.height + padding * 2
      });
    };

    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(target);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step, target]);

  return { target, rect };
}

interface TourOverlayProps {
  resolveAnchor: (anchorId?: string) => HTMLElement | null;
}

export function TourOverlay() {
  const {
    activeTour,
    activeStep,
    nextStep,
    previousStep,
    skipTour,
    closeTour,
    anchorVersion,
    resolveAnchor
  } = useTour();

  if (typeof document === 'undefined' || !activeTour) {
    return null;
  }

  return createPortal(
    <OverlayContent
      activeStep={activeStep}
      activeTour={activeTour}
      nextStep={nextStep}
      previousStep={previousStep}
      skipTour={skipTour}
      closeTour={closeTour}
      anchorVersion={anchorVersion}
      resolveAnchor={resolveAnchor}
    />,
    document.body
  );
}

interface OverlayContentProps {
  activeTour: NonNullable<ReturnType<typeof useTour>['activeTour']>;
  activeStep: TourStep | null;
  nextStep: () => void;
  previousStep: () => void;
  skipTour: () => void;
  closeTour: (options?: { markComplete?: boolean }) => void;
  anchorVersion: number;
  resolveAnchor: (anchorId?: string) => HTMLElement | null;
}

function OverlayContent({
  activeTour,
  activeStep,
  nextStep,
  previousStep,
  skipTour,
  closeTour,
  anchorVersion,
  resolveAnchor
}: OverlayContentProps) {
  const { target, rect } = useSpotlightRect(activeStep, anchorVersion, resolveAnchor);
  const [actionCompleted, setActionCompleted] = useState(!activeStep?.action);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelPosition, setPanelPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    setActionCompleted(!activeStep?.action);
  }, [activeStep?.action, activeStep?.id]);

  useEffect(() => {
    if (!activeStep || activeStep.action?.type !== 'waitForClick' || !target) {
      return;
    }
    const handler = () => {
      setActionCompleted(true);
      if (activeStep.advanceOn !== 'manual') {
        nextStep();
      }
    };
    target.addEventListener('click', handler, { once: true });
    return () => {
      target.removeEventListener('click', handler);
    };
  }, [activeStep, nextStep, target]);

  const isFirstStep = activeTour.stepIndex === 0;
  const isLastStep = !activeStep || activeTour.stepIndex === activeTour.definition.steps.length - 1;
  const canProceed = actionCompleted || !activeStep?.action;

  useLayoutEffect(() => {
    if (!panelRef.current || !rect) {
      setPanelPosition(null);
      return;
    }
    const { offsetWidth, offsetHeight } = panelRef.current;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 16;
    const placement = activeStep?.placement ?? 'bottom';
    let top = rect.top + rect.height + padding;
    let left = rect.left + rect.width / 2 - offsetWidth / 2;

    if (placement === 'top') {
      top = rect.top - offsetHeight - padding;
    } else if (placement === 'left') {
      top = rect.top + rect.height / 2 - offsetHeight / 2;
      left = rect.left - offsetWidth - padding;
    } else if (placement === 'right') {
      top = rect.top + rect.height / 2 - offsetHeight / 2;
      left = rect.left + rect.width + padding;
    } else if (placement === 'auto') {
      if (rect.top < viewportHeight / 2) {
        top = rect.top + rect.height + padding;
      } else {
        top = rect.top - offsetHeight - padding;
      }
    }

    const clampedTop = Math.max(padding, Math.min(top, viewportHeight - offsetHeight - padding));
    const clampedLeft = Math.max(padding, Math.min(left, viewportWidth - offsetWidth - padding));
    setPanelPosition({ top: clampedTop, left: clampedLeft });
  }, [activeStep?.placement, rect]);

  const overlayBlocks = useMemo(() => {
    if (!rect) {
      return <div className="absolute inset-0 bg-black/60" />;
    }
    const { top, left, width, height } = rect;
    const bottom = top + height;
    const right = left + width;
    return (
      <>
        <div
          className="absolute left-0 top-0 bg-black/60"
          style={{
            height: top,
            width: '100%'
          }}
        />
        <div
          className="absolute bg-black/60"
          style={{
            top,
            left: 0,
            width: left,
            height
          }}
        />
        <div
          className="absolute bg-black/60"
          style={{
            top,
            left: right,
            width: Math.max(window.innerWidth - right, 0),
            height
          }}
        />
        <div
          className="absolute left-0 bg-black/60"
          style={{
            top: bottom,
            height: Math.max(window.innerHeight - bottom, 0),
            width: '100%'
          }}
        />
      </>
    );
  }, [rect]);

  const handleClose = () => closeTour({ markComplete: true });

  const spotlightStyle = rect
    ? {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      }
    : null;

  return (
    <React.Fragment>
      <div className="fixed inset-0 z-[1000] pointer-events-none">
        <div className="absolute inset-0 pointer-events-none">{overlayBlocks}</div>
        {spotlightStyle ? (
          <div
            className="pointer-events-none absolute rounded-xl border-2 border-primary shadow-[0_0_0_9999px_rgba(0,0,0,0)]"
            style={{ ...spotlightStyle, boxShadow: '0 0 0 9999px rgba(2,6,23,0.65)' }}
          />
        ) : null}
        {rect && (
          <div
            className="pointer-events-none absolute rounded-lg border border-white/60"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              boxShadow: '0 0 30px rgba(15,23,42,0.45)',
              mixBlendMode: 'lighten'
            }}
          />
        )}
        <div className="pointer-events-none absolute inset-0">
          <div
            ref={panelRef}
            style={
              panelPosition
                ? {
                    position: 'absolute',
                    top: panelPosition.top,
                    left: panelPosition.left
                  }
                : { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
            }
            className="pointer-events-auto max-w-sm rounded-2xl bg-card text-card-foreground shadow-2xl ring-1 ring-border/70"
          >
            <header className="flex items-start justify-between gap-4 border-b border-border/60 px-5 py-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Step {activeTour.stepIndex + 1} of {activeTour.definition.steps.length}
                </p>
                <h3 className="text-base font-semibold text-foreground">{activeStep?.title ?? 'Tour'}</h3>
              </div>
              <Button variant="ghost" size="icon" aria-label="Close tour" onClick={handleClose}>
                ×
              </Button>
            </header>
            <div className="space-y-3 px-5 py-4 text-sm text-muted-foreground">
              {activeStep ? activeStep.description : 'Follow the guided steps to learn this view.'}
              {activeStep?.action?.type === 'waitForClick' && !actionCompleted ? (
                <p className="text-xs font-medium text-amber-500">Click the highlighted element to continue.</p>
              ) : null}
              {!rect ? (
                <p className="text-xs text-destructive">We could not find the highlighted element. Try scrolling the page.</p>
              ) : null}
            </div>
            <footer className="flex items-center justify-between border-t border-border/60 px-5 py-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={skipTour}>
                  Skip tour
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={previousStep} disabled={isFirstStep}>
                  Back
                </Button>
                <Button size="sm" onClick={nextStep} disabled={!canProceed}>
                  {isLastStep ? 'Finish' : 'Next'}
                </Button>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
